/**
 * Investor read path for score events — the `investor_score_events` RPC.
 *
 * WHY NOT `.from('score_event')`
 * ------------------------------
 * A score_event row carries the founder's raw scoring inputs (`input_payload`:
 * deck text, questionnaire text, data-room file names, claims, interview
 * quotes; `llm_raw_output`) next to the score. The owner rule (2026-10-19):
 *   - an investor with score access (DD report, dealflow, submission, actively
 *     raising) sees the score and the AI explanations, with evidence as BACKING
 *     STATUS only (`evidenceSummary.citations`, `claimsSummary` counts) and the
 *     numeric DD metrics;
 *   - with a live data-room grant ("the data room is a pass to all claims and
 *     proof points") — or as a member / admin — the full row.
 * RLS is row-level and cannot express "these columns only with a grant", so
 * investors have no SELECT policy on the table (20261018130000) and read this
 * SECURITY DEFINER function, which applies `score_event_investor_view`.
 * `raw_inputs_visible` says which shape came back.
 *
 * Founders keep reading their own full rows through `score-queries.ts`, which
 * also hosts the single-startup investor hook (`useInvestorLatestScore`).
 * A direct table read from an investor surface returns 0 rows — guarded by
 * `__tests__/investor-score-events.test.ts`.
 */
import { rpc } from './rpc';
import type { ScoreEventReadModel } from './edge-functions';

/** One score event as `investor_score_events` returns it for the caller. */
export interface InvestorScoreEventRow {
  id: string;
  startup_id: string;
  computed_at: string;
  methodology_version: string;
  composite: ScoreEventReadModel['composite'];
  /** Full with a grant; `{ stage, domain }` without. */
  weights_used: { stage?: string; domain?: string } | null;
  dimensions: ScoreEventReadModel['dimensions'];
  risks: ScoreEventReadModel['risks'];
  /** The raw payload with a grant; the numeric DD metrics (`richExtraction`) without. */
  input_payload: Record<string, unknown> | null;
  /** Only with a grant (else null). Not used by any screen. */
  llm_raw_output: unknown | null;
  /** False: quotes, sources, claim text and raw inputs were withheld. */
  raw_inputs_visible: boolean;
}

/**
 * Score events for `startupIds` that the caller may see, in the caller's shape, newest first
 * within each startup. `latestOnly` returns one row per startup.
 *
 * Throws the RPC error (e.g. 42501 when the MFA step-up is pending): a failed
 * read is not an empty read, and callers decide how to surface it.
 */
export async function fetchInvestorScoreEvents(
  startupIds: readonly string[],
  opts: { latestOnly?: boolean } = {},
): Promise<InvestorScoreEventRow[]> {
  if (startupIds.length === 0) return [];
  const { data, error } = await rpc('investor_score_events', {
    p_startup_ids: [...startupIds],
    p_latest_only: opts.latestOnly ?? false,
  });
  if (error) throw error;
  return (data ?? []) as unknown as InvestorScoreEventRow[];
}
